import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Alert } from "@amzn/awsui-components-react/polaris";

import { RootState } from "../app/rootReducer";
import { trackComponentView } from "../telemetry/telemetry";
import { formatMatchCount } from "../validation/validationDashboardTable/formatMatchCount";
import { OCL_PAGE_SIZE } from "./oclPageSize";

/**
 * Warns when the applied filter matches more rows than the One Click
 * Launch tab can load. OCL issues a single `fetchPorAsins(0,
 * OCL_PAGE_SIZE, ...)` and has no pagination, so rows beyond the cap
 * are dropped silently: the header counter, the Launch Scope Summary,
 * and the published launch payload all describe only the first page.
 * Without this banner a user filtering to 600 ASINs would launch 500
 * of them with nothing on screen hinting at the other 100.
 *
 * `totalResultCount` comes back on the same `/search` response the
 * table already fetches (stored as `porAsinList.count`), so naming
 * the real total costs no extra request.
 *
 * Deliberately not dismissible — dismissing would restore exactly the
 * silent-truncation state this exists to prevent.
 */
export const OclRowLimitAlert: React.FC = () => {
  // `searchPorAsinStart` only zeroes `count` when the queryString
  // changes, so a same-query refetch (sort, post-save refresh) keeps
  // the previous total. Gate on `isLoading` so that stale count
  // doesn't flash the banner mid-flight.
  const { count, isLoading } = useSelector(
    (state: RootState) => state.porAsinList
  );

  const truncated = !isLoading && count > OCL_PAGE_SIZE;

  useEffect(() => {
    if (truncated) {
      trackComponentView("oneClickLaunchTable", "rowLimitAlert", {
        matchCount: count,
      });
    }
  }, [truncated, count]);

  if (!truncated) {
    return null;
  }

  return (
    <Alert
      data-testid="ocl-row-limit-alert"
      type="warning"
      statusIconAriaLabel="Warning"
      header={`Only the first ${formatMatchCount(
        OCL_PAGE_SIZE
      )} rows are shown`}
    >
      {`Your filter matches ${formatMatchCount(count)} rows, but this page ` +
        `loads at most ${formatMatchCount(OCL_PAGE_SIZE)}. Launching now ` +
        `affects only the ${formatMatchCount(
          OCL_PAGE_SIZE
        )} rows below. Narrow your filters so every ASIN you intend to ` +
        `launch is visible.`}
    </Alert>
  );
};

export default OclRowLimitAlert;
